import React, { useState, useRef, useCallback, useEffect } from 'react';
import { API, COLORS } from '../utils/constants';
import { r1 } from '../utils/helpers';
import Popup from './Popup';
import SwipeableRow from './SwipeableRow';
import BarraSemaforo from './BarraSemaforo';

// Franjas horarias (sin Pecados ni Picoteo)
const MEAL_SECTIONS = [
  { id: 'Desayuno',           icon: '☀️',  label: 'Desayuno',            lastTime: '06:59:59' },
  { id: 'Media mañana',       icon: '🥐',  label: 'Media mañana',        lastTime: '09:44:59' },
  { id: 'Almuerzo',           icon: '🍽️', label: 'Almuerzo',            lastTime: '12:59:59' },
  { id: 'Pre-entrenamiento',  icon: '💪',  label: 'Pre-entrenamiento',   lastTime: '16:59:59' },
  { id: 'Cena',               icon: '🌆',  label: 'Cena',                lastTime: '22:59:59' },
];

function normalizeTipoComida(tc) {
  if (!tc) return 'Cena';
  const t = tc.toLowerCase();
  if (t.includes('desayuno')) return 'Desayuno';
  if (t.includes('media') || t.includes('mañana')) return 'Media mañana';
  if (t.includes('almuerzo')) return 'Almuerzo';
  if (t.includes('pre')) return 'Pre-entrenamiento';
  return 'Cena';
}

function getCalColor(calIn, burned) {
  const hasIn = calIn > 0;
  const hasBurned = burned > 0;
  if (!hasIn && !hasBurned) return "rgba(255,255,255,0.15)"; // gris
  if (!hasIn || !hasBurned) return "#ffffff";                 // blanco
  if (calIn > burned + 100) return "#ff2d55";                // rojo
  if (calIn < burned - 100) return "#f9c74f";                // amarillo
  return COLORS.green;                                         // verde
}

const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DAY_LABELS = ["L","M","X","J","V","S","D"];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay(); // 0 is Sunday
  const numDays = new Date(year, month + 1, 0).getDate();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1; // 0 for Monday
  const days = [];
  // Fill leading slots with prev-month days (so they appear with dots)
  if (startOffset > 0) {
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const prevNumDays = new Date(prevYear, prevMonth + 1, 0).getDate();
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = prevNumDays - i;
      const dStr = `${prevYear}-${String(prevMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      days.push({ num: d, dateStr: dStr, prevMonth: true });
    }
  }
  for (let d = 1; d <= numDays; d++) {
    const dStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    days.push({ num: d, dateStr: dStr });
  }
  return days;
}

function formatDateES(dStr) {
  const p = dStr.split('-');
  return `${parseInt(p[2])} ${MONTH_NAMES[parseInt(p[1])-1].substring(0,3).toLowerCase()}`;
}

export default function TodayLogPopup({ open, onClose, foods: globalFoods, nutrition: globalNutrition, onDelete, onEdit, onReorder, onAddToSection }) {
  const todayStr = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [localFoods, setLocalFoods] = useState([]);
  
  // Calendar UI state
  const [calOpen, setCalOpen] = useState(false);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [monthlyBalance, setMonthlyBalance] = useState({});
  const [balanceLoaded, setBalanceLoaded] = useState(false);

  const [healthData, setHealthData] = useState({});

  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const longPressTimer = useRef(null);
  const dragStartPos = useRef(null);
  const isDragging = useRef(false);
  const scrollRaf = useRef(null);

  const SCROLL_ZONE = 80;
  const SCROLL_MAX  = 12;

  // Sync data depending on selectedDate
  useEffect(() => {
    if (!open) return;
    if (selectedDate === todayStr) {
      setLocalFoods((globalFoods || []).map(f => ({
        ...f,
        carbs: f.carbs ?? f.carbos ?? 0,
        protein: f.protein ?? f.proteina ?? 0,
        calories: f.calories ?? f.calorias ?? 0,
        fat: f.fat ?? f.grasa ?? 0,
      })));
    } else {
      fetch(`${API}/food-log/${selectedDate}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
            const arr = Array.isArray(data) ? data.map(f => ({
                id: f.id, name: f.nombre, calories: f.calorias, protein: f.proteina,
                carbs: f.carbos, fat: f.grasa, grams: f.gramos, time: f.hora?.slice(0,5),
                tipo_comida: f.tipo_comida
            })) : [];
            setLocalFoods(arr);
        })
        .catch(() => setLocalFoods([]));
    }
  }, [open, selectedDate, todayStr, globalFoods]);

  // Fetch health data (burned calories) for selected date
  useEffect(() => {
    if (!open) return;
    fetch(`${API}/health-sync/${selectedDate}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setHealthData(data || {}))
      .catch(() => setHealthData({}));
  }, [open, selectedDate]);

  // Fetch month summary for dots — also fetches prev month to cover leading days shown in grid
  useEffect(() => {
    if (open) {
      const prevM = calMonth === 0 ? 12 : calMonth;
      const prevY = calMonth === 0 ? calYear - 1 : calYear;
      Promise.all([
        fetch(`${API}/nutricion/resumen-mes?year=${calYear}&month=${calMonth + 1}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/nutricion/resumen-mes?year=${prevY}&month=${prevM}`, { credentials: 'include' }).then(r => r.json()),
      ]).then(([cur, prev]) => {
        setMonthlyBalance({ ...(prev || {}), ...(cur || {}) });
        setBalanceLoaded(true);
      }).catch(() => { setBalanceLoaded(true); });
    }
  }, [open, calYear, calMonth]);

  const handleClose = () => {
    setCalOpen(false);
    setSelectedDate(todayStr);
    setBalanceLoaded(false);
    onClose();
  };

  const isToday = selectedDate === todayStr;

  const handleDelete = (f, idx) => {
    if (isToday) onDelete(f, idx);
    else {
      if (f.id) fetch(API + `/food-log/${f.id}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
      setLocalFoods(localFoods.filter((_, i) => i !== idx)); // Optimistic UI
    }
  };

  const handleEdit = (f, idx) => {
    onEdit(f, idx, selectedDate);
  };

  const stopAutoScroll = useCallback(() => {
    if (scrollRaf.current) {
      cancelAnimationFrame(scrollRaf.current);
      scrollRaf.current = null;
    }
  }, []);

  const startAutoScroll = useCallback((touchY) => {
    stopAutoScroll();
    const el = document.getElementById('today-log-scroll');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const distTop    = touchY - rect.top;
    const distBottom = rect.bottom - touchY;

    let speed = 0;
    if (distTop < SCROLL_ZONE)    speed = -SCROLL_MAX * (1 - distTop / SCROLL_ZONE);
    else if (distBottom < SCROLL_ZONE) speed =  SCROLL_MAX * (1 - distBottom / SCROLL_ZONE);

    if (speed === 0) return;

    const tick = () => {
      el.scrollBy({ top: speed, behavior: 'instant' });
      scrollRaf.current = requestAnimationFrame(tick);
    };
    scrollRaf.current = requestAnimationFrame(tick);
  }, [stopAutoScroll]);

  const handleTouchStart = useCallback((idx, e) => {
    const touch = e.touches[0];
    dragStartPos.current = { x: touch.clientX, y: touch.clientY };
    isDragging.current = false;
    longPressTimer.current = setTimeout(() => {
      isDragging.current = true;
      setDragIdx(idx);
      setDragPos({ x: touch.clientX, y: touch.clientY });
    }, 500);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (longPressTimer.current) {
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - (dragStartPos.current?.x || 0));
      const dy = Math.abs(touch.clientY - (dragStartPos.current?.y || 0));
      if (dx > 8 || dy > 8) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
    if (!isDragging.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    setDragPos({ x: touch.clientX, y: touch.clientY });
    startAutoScroll(touch.clientY);

    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (el) {
      const sectionEl = el.closest('[data-meal-section]');
      if (sectionEl) setDragOverSection(sectionEl.getAttribute('data-meal-section'));
      else setDragOverSection(null);
    }
  }, [startAutoScroll]);

  const handleTouchEnd = useCallback(async () => {
    clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    stopAutoScroll();
    if (!isDragging.current || dragIdx === null) {
      isDragging.current = false;
      setDragIdx(null);
      setDragOverSection(null);
      return;
    }
    isDragging.current = false;
    if (dragOverSection && dragOverSection !== normalizeTipoComida(localFoods[dragIdx]?.tipo_comida)) {
      const sec = MEAL_SECTIONS.find(s => s.id === dragOverSection);
      if (sec) {
          if (isToday) {
              if (onReorder) onReorder(dragIdx, dragOverSection, sec.lastTime);
          } else {
              const food = localFoods[dragIdx];
              if (food) {
                  const newF = localFoods.map((f, i) => i === dragIdx ? { ...f, tipo_comida: dragOverSection, time: sec.lastTime.slice(0, 5) } : f);
                  setLocalFoods(newF);
                  if (food.id) {
                    await fetch(API + `/food-log/${food.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ tipo_comida: dragOverSection, hora: sec.lastTime }),
                    }).catch(() => {});
                  }
              }
          }
      }
    }
    setDragIdx(null);
    setDragOverSection(null);
  }, [dragIdx, dragOverSection, localFoods, onReorder, isToday]);

  if (!open) return null;

  const totalCal  = localFoods.reduce((s, f) => s + (f.calories || 0), 0);
  const totalProt = localFoods.reduce((s, f) => s + (f.protein || 0), 0);
  const totalCarb = localFoods.reduce((s, f) => s + (f.carbs || 0), 0);
  const totalFat  = localFoods.reduce((s, f) => s + (f.fat || 0), 0);
  const burned    = Math.round((healthData.calorias_activas || 0) + (healthData.calorias_reposo || 0));

  const grouped = MEAL_SECTIONS.map(sec => {
    const items = localFoods
      .map((f, i) => ({ ...f, _idx: i }))
      .filter(f => normalizeTipoComida(f.tipo_comida) === sec.id);
    const secCal  = items.reduce((s, f) => s + (f.calories || 0), 0);
    const secProt = items.reduce((s, f) => s + (f.protein || 0), 0);
    const secCarb = items.reduce((s, f) => s + (f.carbs || 0), 0);
    const secFat  = items.reduce((s, f) => s + (f.fat || 0), 0);
    return { ...sec, items, secCal, secProt, secCarb, secFat };
  });

  const monthGrid = getMonthDays(calYear, calMonth);

  return (
    <Popup onClose={handleClose} title="" zIndex={600} flex={false} userSelect="none" scrollId="today-log-scroll">
      <style>{`.today-log-popup, .today-log-popup * { user-select: none; -webkit-user-select: none; }`}</style>
      <div className="today-log-popup" style={{ userSelect: 'none', WebkitUserSelect: 'none', marginTop: -15 }}>

      {/* Título y Selector de Fecha Dinámico */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        {isToday ? (
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', textAlign: 'center' }}>
                Qué has comido <span 
                  onClick={() => setCalOpen(!calOpen)}
                  style={{ color: COLORS.accent, textDecoration: 'underline', textDecorationColor: 'rgba(253,139,0,0.4)', textUnderlineOffset: 4, cursor: 'pointer' }}
                >hoy</span>
            </div>
        ) : (
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                Qué comiste el <span 
                  onClick={() => setCalOpen(!calOpen)}
                  style={{ color: COLORS.accent, cursor: 'pointer', background: 'rgba(253,139,0,0.1)', padding: '2px 10px', borderRadius: 8 }}
                >{formatDateES(selectedDate)}</span>
                <button 
                  onClick={() => { setSelectedDate(todayStr); setCalOpen(false); }}
                  style={{ background: 'rgba(255,255,255,0.1)', border: 'none', padding: '6px 12px', borderRadius: 12, color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                >
                  VOLVER A HOY
                </button>
            </div>
        )}
      </div>

      {calOpen && (
          <div style={{ background: 'rgba(30,30,35,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 16, marginBottom: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
              {/* Controles Mes */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <button onClick={() => {
                      if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
                      else setCalMonth(calMonth - 1);
                  }} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>{"<"}</button>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{MONTH_NAMES[calMonth]} {calYear} <span style={{fontSize:10,color:'rgba(255,255,255,0.4)'}}>[{Object.keys(monthlyBalance).length}]</span></div>
                  <button onClick={() => {
                      if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
                      else setCalMonth(calMonth + 1);
                  }} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>{">"}</button>
              </div>

              {/* Cabecera Días */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
                  {DAY_LABELS.map(dl => (
                      <div key={dl} style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 700 }}>{dl}</div>
                  ))}
              </div>

              {/* Grilla Días */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                  {monthGrid.map((dObj, i) => {
                      if (!dObj) return <div key={i} />;
                      const isSel = selectedDate === dObj.dateStr;
                      const bal = (monthlyBalance || {})[dObj.dateStr];
                      const dotColor = balanceLoaded
                        ? getCalColor(bal ? (bal.calIn || 0) : 0, bal ? (bal.burned || 0) : 0)
                        : "rgba(255,255,255,0.15)";
                      return (
                          <div 
                              key={dObj.dateStr} 
                              onClick={() => { setSelectedDate(dObj.dateStr); setCalOpen(false); }}
                              style={{ 
                                  background: isSel ? COLORS.accent : 'rgba(255,255,255,0.03)', 
                                  borderRadius: 10, padding: '8px 0', 
                                  display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer',
                                  border: isSel ? `1px solid ${COLORS.accent}` : '1px solid rgba(255,255,255,0.02)'
                              }}
                          >
                              <div style={{ color: isSel ? '#000' : dObj.prevMonth ? 'rgba(255,255,255,0.25)' : '#fff', fontSize: 14, fontWeight: isSel ? 800 : 500 }}>{dObj.num}</div>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, marginTop: 3, flexShrink: 0 }} />
                          </div>
                      );
                  })}
              </div>
          </div>
      )}

      {/* Cabecera macros */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {[
          ['kcal', r1(totalCal),  COLORS.accent],
          ['prot', r1(totalProt) + 'g', COLORS.blue],
          ['carb', r1(totalCarb) + 'g', COLORS.green],
          ['gras', r1(totalFat)  + 'g', COLORS.purple],
        ].map(([label, val, color]) => (
          <div key={label} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '8px 4px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ color, fontSize: 15, fontWeight: 800, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{val}</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 600, letterSpacing: 0.3, marginTop: 2 }}>{label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Barra semáforo — balance calórico */}
      <BarraSemaforo calIn={totalCal} burned={burned} style={{ marginBottom: 18 }} />

      {/* Lista vacía */}
      {localFoods.length === 0 && (
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, textAlign: 'center', margin: '24px 0' }}>Nada registrado el {isToday ? 'día de hoy' : formatDateES(selectedDate)}</p>
      )}

      {/* Secciones por franja horaria */}
      <div
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
      >
        {grouped.map(sec => (
          <div
            key={sec.id}
            data-meal-section={sec.id}
            style={{
              marginBottom: 14,
              borderRadius: 14,
              border: dragOverSection === sec.id ? `1px solid ${COLORS.accent}` : '1px solid transparent',
              background: dragOverSection === sec.id ? 'rgba(253,139,0,0.06)' : 'transparent',
              transition: 'border 0.15s, background 0.15s',
            }}
          >
            {/* Cabecera sección: icono + label + macros acumulados + botón + */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '4px 4px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14 }}>{sec.icon}</span>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase' }}>{sec.label}</span>
                </div>
                {sec.items.length > 0 && (
                  <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, paddingLeft: 2 }}>
                    <span style={{ color: COLORS.accent, fontWeight: 700 }}>{r1(sec.secCal)} kcal</span>
                    {' · '}{r1(sec.secProt)}g p · {r1(sec.secCarb)}g c · {r1(sec.secFat)}g g
                  </div>
                )}
              </div>
              <button
                onTouchStart={e => e.stopPropagation()}
                onClick={e => { e.stopPropagation(); onAddToSection(sec.id, selectedDate); }}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, width: 28, height: 28, color: 'rgba(255,255,255,0.6)', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, flexShrink: 0, marginTop: 2 }}
              >+</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sec.items.length === 0 ? (
                <div style={{
                  border: dragOverSection === sec.id
                    ? `1.5px dashed ${COLORS.accent}`
                    : '1.5px dashed rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  textAlign: 'center',
                  color: dragOverSection === sec.id ? COLORS.accent : 'rgba(255,255,255,0.2)',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  transition: 'border 0.15s, color 0.15s',
                }}>
                  {dragOverSection === sec.id ? '⬇ Soltar aquí' : 'Vacío'}
                </div>
              ) : (
                sec.items.map((f) => (
                  <SwipeableRow
                    key={f.id || f._idx}
                    onDelete={() => handleDelete(f, f._idx)}
                  >
                    <div
                      onTouchStart={(e) => handleTouchStart(f._idx, e)}
                      style={{
                        background: dragIdx === f._idx ? 'rgba(253,139,0,0.12)' : 'rgba(255,255,255,0.03)',
                        borderRadius: 12,
                        padding: '10px 12px',
                        border: dragIdx === f._idx ? `1px solid ${COLORS.accent}60` : '1px solid rgba(255,255,255,0.05)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        touchAction: 'none',
                        transition: 'background 0.15s',
                      }}
                    >
                      <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 15, marginRight: 8, flexShrink: 0, cursor: 'grab' }}>⠿</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {f.name}
                          {f.grams > 0 && <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}> · {r1(f.grams)}g</span>}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginTop: 2 }}>
                          {r1(f.protein)}g p · {r1(f.carbs)}g c · {r1(f.fat)}g g
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: COLORS.accent, fontWeight: 800, fontSize: 14 }}>{r1(f.calories)} kcal</div>
                          {f.time && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>{f.time}</div>}
                        </div>
                        <button
                          onTouchStart={e => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); handleEdit(f, f._idx); }}
                          style={{ background: 'none', border: 'none', color: COLORS.blue, fontSize: 17, cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}
                        >✎</button>
                      </div>
                    </div>
                  </SwipeableRow>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {dragIdx !== null && (
        <div
          style={{
            position: 'fixed',
            left: dragPos.x - 80,
            top: dragPos.y - 30,
            width: 160,
            background: `linear-gradient(135deg, rgba(253,139,0,0.9), rgba(229,90,43,0.9))`,
            borderRadius: 12,
            padding: '8px 14px',
            color: '#fff',
            fontSize: 12,
            fontWeight: 800,
            pointerEvents: 'none',
            zIndex: 10000,
            boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {localFoods[dragIdx]?.name || ''}
        </div>
      )}
      </div>
    </Popup>
  );
}
